import { forwardRef } from 'react';

import { Typography } from '@mui/material';
import { ChevronDownDashboardIcon as ChevronDownIcon } from '@truvhq/ui';
import { clsx } from 'clsx';
import { motion, useReducedMotion } from 'framer-motion';

import type { ChecklistStep, StepStatus } from 'entities/onboarding/types';
import { toYouTubeEmbedUrl } from 'features/onboarding/lib/video';
import { ChecklistStepActions } from 'features/onboarding/ui/checklistStep/ChecklistStepActions';
import { StepMarker } from 'features/onboarding/ui/checklistStep/StepMarker';
import { StepVideo } from 'features/onboarding/ui/checklistStep/StepVideo';

/** The design's collapse curve, shared with the pane so header and rows open on one motion. */
export const COLLAPSE_TRANSITION = { duration: 0.24, ease: [0.22, 0.7, 0.3, 1] } as const;

type ChecklistStepRowProps = {
  step: ChecklistStep;
  blockerTitle: string | null;
  isExpanded: boolean;
  onExpand: () => void;
  onOpenBlocker: () => void;
  onSettle: (status: StepStatus) => Promise<unknown>;
  busy: StepStatus | null;
};

export const ChecklistStepRow = forwardRef<HTMLLIElement, ChecklistStepRowProps>(function ChecklistStepRow(
  { step, blockerTitle, isExpanded, onExpand, onOpenBlocker, onSettle, busy },
  ref,
) {
  const isSettled = step.status === 'complete' || step.status === 'skipped';
  const embedUrl = toYouTubeEmbedUrl(step.video_url);
  const prefersReducedMotion = useReducedMotion();

  return (
    <li
      ref={ref}
      className={clsx('rounded-xl border bg-white', isExpanded ? 'border-primary-bright' : 'border-grey30')}
    >
      {/* The card's padding lives on the button, so every part of a collapsed row opens it. */}
      <button
        type="button"
        onClick={onExpand}
        aria-expanded={isExpanded}
        className="flex w-full cursor-pointer items-start gap-3 bg-transparent px-3 py-2.5 text-left"
      >
        <StepMarker status={step.status} />
        <Typography className={clsx('min-w-0 flex-1 text-sm', isSettled ? 'text-grey60' : 'font-medium text-grey90')}>
          {step.title}
        </Typography>
        <motion.span
          className="mt-0.5 flex shrink-0"
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={prefersReducedMotion ? { duration: 0 } : COLLAPSE_TRANSITION}
          aria-hidden
        >
          <ChevronDownIcon className="h-5 w-5 text-grey60" />
        </motion.span>
      </button>

      {/* Nothing inside moves — the card just reveals what is already laid out. Hidden once closed,
          so the controls in here leave the tab order. */}
      <motion.div
        className="overflow-hidden"
        initial={false}
        animate={{ height: isExpanded ? 'auto' : 0, opacity: isExpanded ? 1 : 0 }}
        transition={prefersReducedMotion ? { duration: 0 } : COLLAPSE_TRANSITION}
        aria-hidden={!isExpanded}
      >
        <div className="px-3 pb-2.5 pl-11">
          {step.description && (
            <Typography variant="body2" className="text-grey70">
              {step.description}
            </Typography>
          )}
          {embedUrl && <StepVideo embedUrl={embedUrl} title={step.title} isExpanded={isExpanded} />}
          <ChecklistStepActions
            step={step}
            blockerTitle={blockerTitle}
            onOpenBlocker={onOpenBlocker}
            busy={busy}
            onSettle={onSettle}
          />
        </div>
      </motion.div>
    </li>
  );
});
